import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "../../ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { Input } from "../../ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../../ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../ui/dialog";
import { FiEdit } from "react-icons/fi";
import {
  convertToUTC,
  convertUtcToLocalTime,
} from "../../../lib/utils";
import { updateScheduleScanWithId } from "../../../helpers/api";

const UpdateScheduleScanModal = ({ data }) => {
  console.log(data);
  const [open, setOpen] = useState(false);
  const scanId = data?.id;

  const scheduleField = {
    scheduleType: z.string().min(1, "Please select a type"),
    startDate: z.string().min(1, "Please select a date"),
    startTime: z.string().min(1, "Please select a time"),
  };

  const formSchema = z.object(scheduleField);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      scheduleType: data.scheduleType,
      startDate: data.startDate,
      startTime: convertUtcToLocalTime(data.startTime),
    },
  });

  useEffect(() => {
    if (data) {
      form.reset({
        scheduleType: data.scheduleType || "",
        startDate: data.startDate || "",
        startTime: convertUtcToLocalTime(data.startTime) || "",
      });
    }
  }, [data, form]);

  const queryClient = useQueryClient();
  const { mutate, isPending } = useMutation({
    mutationFn: ({ id, data }) => updateScheduleScanWithId(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["scans"] });
      toast("Successful");
      setOpen(false);
    },
    onError: (data) => {
      toast(data.message);
    },
  });

  const onSubmit = (data) => {
    const payload = {
      schedule: {
        type: data?.scheduleType,
        startDate: data?.startDate,
        startTime: convertToUTC(data?.startTime?.slice(0, 5)),
      },
    };
    mutate({ id: scanId, data: payload });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen} inert>
      <DialogTrigger asChild>
        <Button className="w-fit bg-neutral200" variant={"ghost"}>
          <FiEdit size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="">
        <DialogHeader className="text-left">
          <DialogTitle className="text-md text-semibold text-black">
            Update Scan Schedule
          </DialogTitle>
          <DialogDescription>
            Change the frequency and time for running scheduled scans
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-4 mt-4"
          >
            <FormField
              control={form.control}
              name="scheduleType"
              render={({ field }) => (
                <FormItem className="mb-4">
                  <FormLabel>Schedule Type</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field?.value}
                  >
                    <FormControl>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select a type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="one-off">Once</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="md:flex gap-4 justify-between items-center">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => {
                  return (
                    <FormItem className="flex flex-col md:w-1/2 mb-4">
                      <FormLabel>Start Date</FormLabel>
                      <FormControl>
                        <Input {...field} type="date" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />

              <FormField
                control={form.control}
                name="startTime"
                render={({ field }) => (
                  <FormItem className="flex flex-col md:w-1/2 mb-4">
                    <FormLabel>Start time</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="time"
                        id="time-picker"
                        step="1"
                        className="bg-background [&::-webkit-calendar-picker-indicator]:mr-0 "
                      />
                    </FormControl>

                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-end space-x-4 pt-4">
              <Button
                type="submit"
                className="px-4 py-2 font-medium text-white rounded-full"
                loading={isPending}
              >
                Update
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UpdateScheduleScanModal;
